import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootState, AppDispatch } from '@store/index'
import { Modal, Row, Col } from 'react-bootstrap'
import Stepper from '@components/common/Stepper/Stepper.component'
import Button from '@components/common/Button/Button.component'
import ClaimDetailsTab from './ClaimDetailsTab'
import ClaimProcedureTab from './ClaimProcedureTab'
import ClaimAttachmentTab from './ClaimAttachmentTab'
import ClaimReviewTab from './ClaimReviewTab'
import EditClaimProcedureModal from '../EditClaimProcedureModal/EditClaimProcedureModal.component'
import {
  Attachment,
  Claim,
  Procedure,
  ProcedureSource,
  ProcedureStatus,
} from '@dataTypes/claimFeature.types'
import { initialClaim } from './initialClaimData'
import ConfirmationModal from '@components/common/ConfirmationModal/ConfirmationModal.component'
import { Modal as DsModal } from '@components/ui'
import { isClaimApproved } from '@utils/claimUtils'
import { useAppSelector } from '@store/hooks'
import { GetInsurancePlansByPatientId } from '@services/api/insurance.api'
import { Provider } from '@dataTypes/provider.types'
import {
  getProviderSettings,
  searchProvidersByName,
} from '@services/api/provider.api'
import { getAllApptProceduresByPatientId } from '@services/api/appointment.api'
import {
  createClaimByPatientId,
  saveDraftClaim,
  updateClaimById,
} from '@services/api/claims.api'
import { submitClaim } from '@store/actions/claims.action'
import moment from 'moment'
import { ProviderSettingsType } from '@dataTypes/procedureSettings.type'
import { generateId } from '@utils/idGenerator.util'
import {
  checkAttachmentRequirementsExternal,
  submitAttachmentExternal,
} from '@services/api/attachment.api'
import { fileFromUrl, fileToBase64 } from '@utils/file.utils'
import { getTreatmentPlanByPatient } from '@services/api/treatment-plan.api'
import { getInsuranceFieldValues } from '@utils/insurance.util'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import { addNote } from '@store/actions/note.action'
import { INote, NoteContextType } from '@dataTypes/note.type'
import {
  extractClaimIdFromCreateClaimResponse,
  getNotesClaimContextId,
} from '@utils/claims.util'
import { providerRoles } from '@utils/provider.util'

import { logger } from '@utils/safeLogger'

async function persistUnsavedClaimAttachmentNotes(
  dispatch: AppDispatch,
  patientId: string,
  claimUuid: string,
  notes: INote[] | undefined,
): Promise<void> {
  if (!notes?.length) {
    return
  }
  for (const raw of notes) {
    const n = raw as Partial<INote> & { content?: string }
    if (n.note_id?.trim()) {
      continue
    }
    const title = (n.title ?? 'Untitled').trim() || 'Untitled'
    const body = (n.body ?? n.content ?? '').trim()
    if (!body) {
      continue
    }
    await dispatch(
      addNote({
        patient_id: patientId,
        context_type: NoteContextType.CLAIMS,
        context_id: claimUuid,
        title,
        body,
      }),
    ).unwrap()
  }
}

type AddClaimsModalProps = {
  isVisible: boolean
  closeModal: () => void
  editingClaim?: Claim | null
  activeTab: 'pre-auth' | 'claims' | 'tracking'
  refreshClaims: () => void
}

type ProviderInput = {
  id: string | null
  value: string
  taxId: string
}

const AddClaimsModal: React.FC<AddClaimsModalProps> = ({
  isVisible,
  closeModal,
  editingClaim,
  activeTab,
  refreshClaims,
}) => {
  const dispatch = useDispatch<AppDispatch>()
  const { claims } = useSelector((state: RootState) => state.claimFeature)
  const [step, setStep] = useState<number>(1)
  const [selectedClaim, setSelectedClaim] = useState<Claim>(initialClaim)
  const [editViewProcedureModal, setEditViewProcedureModal] = useState<{
    isVisible: boolean
    editMode: boolean
  }>({
    isVisible: false,
    editMode: false,
  })
  const [selectedProcedure, setSelectedProcedure] = useState<Procedure | null>(
    null,
  )
  const [warningInfo, setWarningInfo] = useState<{
    show: boolean
    title: string
    body: string
  }>({
    show: false,
    title: '',
    body: '',
  })
  const [showSubmitConfirmation, setShowSubmitConfirmation] =
    useState<boolean>(false)
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const { showToast } = useNotificationToast()

  const isApproved = isClaimApproved(selectedClaim)

  const steps: string[] = ['Details', 'Procedures', 'Attachment', 'Review']
  const user = JSON.parse(localStorage.getItem('user') || '{}')
  const selectedClinic = useAppSelector((state) => state.clinic.selectedClinic)
  const selectedPatient = useAppSelector(
    (state) => state.patient.selectedPatient,
  )
  const [insurancePlans, setInsurancePlans] = useState<any[]>([])
  const [selectedInsurancePlan, setSelectedInsurancePlan] = useState<any>({})
  const [selectedProvider, setSelectedProvider] = useState<ProviderInput>({
    id: null,
    value: '',
    taxId: '',
  })
  const [debouncedProviderName, setDebouncedProviderName] = useState('')
  const [providers, setProviders] = useState<Provider[]>([])
  const [appointments, setAppointments] = useState<any[]>([])
  const [treatmentPlans, setTreatmentPlans] = useState<any[]>([])
  const [selectedAppointment, setSelectedAppointment] = useState<any>({})
  const [selectedTreatmentPlan, setSelectedTreatmentPlan] = useState<any>({})
  const [providerSettings, setProviderSettings] = useState<
    ProviderSettingsType[]
  >([])
  const [progressBarModalInfo, setProgressBarModalInfo] = useState<{
    title: string
    show: boolean
  }>({
    title: '',
    show: false,
  })

  const getAllRelatedPatientData = (
    patientId: string,
    claimData?: Claim,
  ): void => {
    if (claimData) {
      showLoading({
        container: '.add-edit-claims-modal',
        hideElements: ['.add-claims-modal-body > .tab-content'],
      })
    }
    GetInsurancePlansByPatientId(patientId).then((response) => {
      const plans = response?.data?.insurancePlans ?? []
      setInsurancePlans(plans)
      if (claimData) {
        setSelectedInsurancePlan(
          plans.find(
            (insurance: any) =>
              insurance.insurance_plan_id === claimData.insurancePlanId,
          ),
        )
      }
    })
    getAllApptProceduresByPatientId(patientId, claimData?.appointmentId).then(
      (response) => {
        const procedures = response?.data?.appointment_procedures ?? []
        setAppointments(procedures)
        if (claimData) {
          setSelectedAppointment(
            procedures.find(
              (appointment: any) =>
                appointment.appointment_id === claimData.appointmentId,
            ),
          )
        }
      },
    )
    getTreatmentPlanByPatient(patientId).then((response) => {
      const plans = response?.data?.treatment_plan ?? []
      setTreatmentPlans(plans)
      if (claimData) {
        setSelectedTreatmentPlan(
          plans.find(
            (treatmentPlan: any) =>
              treatmentPlan.tp_id === claimData.treatmentPlanId,
          ) ?? null,
        )
      }
    })
    getProviderSettings().then((response) => {
      if (response.statusCode === 200 && 'provider_settings' in response.data) {
        setProviderSettings(
          response.data.provider_settings as unknown as ProviderSettingsType[],
        )
      } else {
        setProviderSettings([])
      }
    })

    if (claimData) {
      setSelectedProvider({
        id: claimData.providerId,
        value: claimData.providerName,
        taxId: claimData.providerTaxId,
      })
      setTimeout(() => {
        hideLoading('.add-edit-claims-modal')
      }, 2000)
    }
  }

  useEffect(() => {
    if (!selectedPatient?.patient_id) return
    getAllRelatedPatientData(selectedPatient.patient_id)
  }, [selectedPatient?.patient_id])

  useEffect(() => {
    if (selectedInsurancePlan) {
      let updatedProcedures: Procedure[] = []
      if (selectedClaim.procedures.length > 0) {
        updatedProcedures = selectedClaim.procedures.map((procedure) => {
          const updatedInsuranceFieldValues = getInsuranceFieldValues(
            procedure.procedureId,
            Number(procedure.clinic_fee),
            selectedInsurancePlan.insurance_plan_id,
            insurancePlans,
            selectedProvider.id || '',
          )
          updatedInsuranceFieldValues.amount =
            updatedInsuranceFieldValues.allowed_amount || procedure.clinic_fee
          return {
            ...procedure,
            ...updatedInsuranceFieldValues,
          }
        })
      }
      setSelectedClaim((prevClaim) => ({
        ...prevClaim,
        insurancePlanId: selectedInsurancePlan.insurance_plan_id,
        subscriberId: selectedInsurancePlan.subscriber_member_id,
        subscriberName: selectedInsurancePlan.subscriber_fullname,
        subscriberFirstName: selectedInsurancePlan.subscriber_firstname,
        subscriberLastName: selectedInsurancePlan.subscriber_lastname,
        subscriberDateOfBirth: selectedInsurancePlan.subscriber_dob,
        patientId: selectedInsurancePlan?.patient_member_id,
        patientMemberId: selectedInsurancePlan?.patient_member_id,
        patientName: selectedInsurancePlan?.patient_fullname,
        patientFirstName: selectedInsurancePlan?.patient_firstname,
        patientLastName: selectedInsurancePlan?.patient_lastname,
        patientDateOfBirth: selectedInsurancePlan?.patient_dob,
        patientGender: selectedInsurancePlan?.patient_gender,
        insuranceCarrier: selectedInsurancePlan.carrier_name,
        insuranceCarrierId: selectedInsurancePlan.insurance_plan_id,
        insuranceCarrierPayerCode: selectedInsurancePlan.payer_id,
        procedures: updatedProcedures,
      }))
    }
  }, [selectedInsurancePlan])

  useEffect(() => {
    if (selectedProvider.id) {
      setSelectedClaim((prevClaim) => ({
        ...prevClaim,
        providerId: selectedProvider.id || '',
        providerName: selectedProvider.value,
        providerTaxId: selectedProvider.taxId,
      }))
    }
  }, [selectedProvider])

  useEffect(() => {
    if (selectedAppointment && selectedAppointment.appointment_id) {
      setSelectedClaim((prevClaim) => ({
        ...prevClaim,
        appointmentId: selectedAppointment.appointment_id || '',
      }))
    }
  }, [selectedAppointment])

  useEffect(() => {
    if (selectedTreatmentPlan && selectedTreatmentPlan.tp_id) {
      setSelectedClaim((prevClaim) => ({
        ...prevClaim,
        treatmentPlanId: selectedTreatmentPlan.tp_id || '',
      }))
    }
  }, [selectedTreatmentPlan])

  const handleInsuranceSelect = (insurancePlanId: string): void => {
    if (insurancePlans.length > 0 && insurancePlanId) {
      setSelectedInsurancePlan(
        insurancePlans.find(
          (insurance) => insurance.insurance_plan_id === insurancePlanId,
        ),
      )
    } else {
      setSelectedInsurancePlan({})
    }
  }

  const fetchProviders = async (name: string): Promise<void> => {
    try {
      const response = await searchProvidersByName(name)
      if (response.statusCode === 200) {
        if ('providers' in response.data) {
          const providerList = Array.isArray(response.data?.providers)
            ? response.data.providers
            : []

          const filteredProvidersByClinic = providerList.filter(
            (prov) =>
              prov.clinic_id === selectedClinic?.clinic_id &&
              providerRoles.includes(prov.work_role ?? ''),
          )

          setProviders(filteredProvidersByClinic as Provider[])
        } else {
          setProviders([])
        }
      } else {
        setProviders([])
      }
    } catch (error) {
      logger.error('Error fetching providers:', error)
      setProviders([])
    }
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      if (debouncedProviderName.trim()) {
        fetchProviders(debouncedProviderName.trim())
      } else {
        setProviders([])
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [debouncedProviderName])

  const providerNameHandleChange = (value: string): void => {
    setSelectedProvider({ id: null, value, taxId: '' })
    setDebouncedProviderName(value)
  }

  const handleProviderSelect = (provider: Provider): void => {
    setSelectedProvider({
      id: provider.provider_id,
      value: `Dr. ${provider.first_name} ${provider.last_name}`,
      taxId: provider?.tax_id_number?.trim().replace('-', '') || '',
    })
    setProviders([])
  }

  const getProceduresAttachmentRequirements = async (
    procedureCodes: string[],
  ): Promise<void> => {
    const formData = {
      payerIdCode: selectedClaim.insuranceCarrierPayerCode,
      claimType: selectedClaim.claimType === 'Claims' ? 'CL' : 'PA',
      procedureCodes: procedureCodes,
    }
    const response = await checkAttachmentRequirementsExternal(formData)
    setSelectedClaim((prevClaim) => ({
      ...prevClaim,
      attachmentRequirements: response.data.response,
    }))
  }

  const handleAppointmentSelect = (appointment: any): void => {
    if (!appointment) {
      setSelectedAppointment({})
      setSelectedClaim((prevClaim) => ({
        ...prevClaim,
        appointmentId: '',
        procedures: [],
        claimAmount: '0.00',
      }))
      return
    }
    const procCodes: string[] = []
    const procedures = Object.values(
      appointment?.procedure_logs.reduce((acc: any, procedurelog: any) => {
        const key = procedurelog.proc_id

        const procSettings = procedurelog.procedure.procedure_settings
        const clinicFee =
          procedurelog.clinic_fee != null
            ? Number(procedurelog.clinic_fee)
            : procSettings.clinic_fee

        if (!acc[key]) {
          procCodes.push(procedurelog.procedure.procedure_code)
          acc[key] = {
            procedureId: procedurelog.procedure.procedure_id,
            procedureName: procedurelog.procedure.description,
            procedureCode: procedurelog.procedure.procedure_code,
            procedureDate: moment(procedurelog.proc_date).format('MM/DD/YYYY'),
            procLogId: procedurelog.proc_log_id,
            provider: `Dr. ${appointment.provider.last_name}`,
            providerFirstName: appointment.provider.first_name,
            providerLastName: appointment.provider.last_name,
            coverage: '85%',
            toothNumber: '30',
            toothSurface: 'MO',
            siteRegion:
              procedurelog.area_type === 1
                ? `${procedurelog.tooth}${procedurelog.surf ? '/' + procedurelog.surf : ''}`
                : procedurelog.area,
            ...getInsuranceFieldValues(
              procedurelog.procedure.procedure_id,
              clinicFee,
              selectedInsurancePlan.insurance_plan_id,
              insurancePlans,
              selectedProvider.id || '',
            ),
            status: ProcedureStatus.Unclaimed,
            source: ProcedureSource.Appointment,
            procedureNotes: [],
            procedureAttachments: [],
            patientName: `${appointment.patient.first_name} ${appointment.patient.last_name}`,
          }
          acc[key].amount = acc[key].allowed_amount || clinicFee
        } else {
          let siteRegionValues = acc[key].siteRegion
          if (procedurelog.area_type === 1) {
            siteRegionValues =
              siteRegionValues +
              ', ' +
              procedurelog.tooth +
              `${procedurelog.surf ? '/' + procedurelog.surf : ''}`
          } else {
            siteRegionValues = siteRegionValues + ', ' + procedurelog.siteRegion
          }
          acc[key].siteRegion = siteRegionValues
        }

        return acc
      }, {}),
    )
    const claimAmount = procedures.reduce((acc: any, procedure: any) => {
      return acc + Number(procedure.amount)
    }, 0) as number
    setSelectedAppointment(appointment)
    setSelectedClaim((prevClaim) => ({
      ...prevClaim,
      procedures: procedures as Procedure[],
      claimAmount: claimAmount.toFixed(2),
    }))
    getProceduresAttachmentRequirements(procCodes)
  }

  const handleTreatmentPlanSelect = (treatmentPlan: any): void => {
    if (!treatmentPlan) {
      setSelectedTreatmentPlan({})
      setSelectedClaim((prevClaim) => ({
        ...prevClaim,
        treatmentPlanId: '',
        procedures: [],
        claimAmount: '0.00',
      }))
      return
    }
    const procCodes: string[] = []
    const procedures: Procedure[] = []
    treatmentPlan.phases.forEach((phase: any) =>
      phase.items.forEach((item: any) => {
        procedures.push({
          procedureId: item.procedure_id,
          procedureName: item.procedure_settings.procedure.description,
          procedureCode: item.procedure_settings.procedure.procedure_code,
          procedureDate: moment(phase.scheduled_date).format('MM/DD/YYYY'),
          itemId: item.item_id,
          coverage: '85%',
          amount: item.charges,
          siteRegion: item.surface,
          clinic_fee: item.charges,
          ...getInsuranceFieldValues(
            item.procedure_id,
            item.charges,
            selectedClaim.insurancePlanId,
            insurancePlans,
            selectedProvider.id || '',
          ),
          toothNumber: '30',
          toothSurface: 'MO',
          status: ProcedureStatus.Unclaimed,
          source: ProcedureSource.Appointment,
          procedureNotes: [],
          procedureAttachments: [],
          patientName: `${treatmentPlan.patient.first_name} ${treatmentPlan.patient.last_name}`,
          phaseName: phase.phase_name,
        })
      }),
    )

    let claimAmount = 0
    for (const procedure of procedures) {
      claimAmount += Number(procedure.amount)
    }
    setSelectedTreatmentPlan(treatmentPlan)
    setSelectedClaim((prevClaim) => ({
      ...prevClaim,
      procedures: procedures as Procedure[],
      claimAmount: claimAmount.toFixed(2),
    }))
    getProceduresAttachmentRequirements(procCodes)
  }

  const handleClaimSourceChange = (claimSource: 0 | 1): void => {
    setSelectedClaim((prevClaim) => ({
      ...prevClaim,
      claimSource,
      procedures: [],
      ...(claimSource === 1 && {
        appointmentId: '',
      }),
      ...(claimSource === 0 && {
        treatmentPlanId: '',
      }),
    }))
    setSelectedTreatmentPlan({})
    setSelectedAppointment({})
  }

  const prepareAllEditingData = (claimData: Claim): void => {
    getAllRelatedPatientData(claimData.patientId, claimData)
  }

  const getAttachmentFiles = async (
    attachments: Attachment[],
  ): Promise<void> => {
    const attachmentsUpdated = await Promise.all(
      attachments.map(async (attachment: any) => {
        const file = await fileFromUrl(
          attachment.fileUrl,
          attachment.fileName,
          attachment.mimeType,
        )
        return {
          ...attachment,
          newFile: file,
        }
      }),
    )
    setSelectedClaim((prevClaim) => ({
      ...prevClaim,
      attachments: attachmentsUpdated,
    }))
  }

  const getProceduresAttachmentFiles = async (
    procedures: Procedure[],
  ): Promise<void> => {
    const proceduresUpdated = await Promise.all(
      procedures.map(async (procedure: any) => {
        const attachments = await Promise.all(
          procedure.procedureAttachments.map(async (attachment: any) => {
            const file = await fileFromUrl(
              attachment.fileUrl,
              attachment.fileName,
              attachment.mimeType,
            )
            return {
              ...attachment,
              newFile: file,
            }
          }),
        )
        return {
          ...procedure,
          procedureAttachments: attachments,
        }
      }),
    )
    setSelectedClaim((prevClaim) => ({
      ...prevClaim,
      procedures: proceduresUpdated,
    }))
  }

  useEffect(() => {
    const initialize = async (): Promise<void> => {
      const now = new Date().toISOString().split('T')[0]

      if (editingClaim) {
        setSelectedClaim(editingClaim)
        prepareAllEditingData(editingClaim)
        getAttachmentFiles(editingClaim.attachments)
        getProceduresAttachmentRequirements(
          editingClaim.procedures.map((procedure) => procedure.procedureCode),
        )
        getProceduresAttachmentFiles(editingClaim.procedures)
      } else {
        const providerClaimId = await generateId(
          `${activeTab === 'pre-auth' ? 'PRE' : 'CLM'}`,
          6,
        )
        setSelectedClaim({
          ...initialClaim,
          id: providerClaimId,
          patientIdSystem: selectedPatient?.patient_id || '',
          providerClaimId: providerClaimId,
          createdAt: now,
          updatedAt: now,
          claimType: activeTab === 'pre-auth' ? 'Pre-Authorization' : 'Claims',
        })
      }
    }

    initialize()
  }, [editingClaim, activeTab])

  const handleNext = (): void => {
    if (step === 1) {
      const insurancePlanId = selectedClaim.insurancePlanId
      const providerId = selectedClaim.providerId

      if (insurancePlanId === '' || providerId === '') {
        setWarningInfo({
          show: true,
          title: 'Missing fields',
          body: `You must add an insurance plan and a provider before proceeding to the next step.`,
        })
        return
      }
    }
    if (step === 2 && selectedClaim.procedures.length === 0) {
      setWarningInfo({
        show: true,
        title: 'Missing procedures',
        body: 'You must add at least one procedure before proceeding to the next step.',
      })
      return
    } else if (step === 2 && selectedClaim.procedures.length > 0) {
      const attachmentIncomplete = document.querySelectorAll(
        '.procedure-attachment-incomplete-icon',
      )
      if (attachmentIncomplete.length > 0) {
        setWarningInfo({
          show: true,
          title: 'Missing attachments',
          body: 'You must add all attachments before proceeding to the next step.',
        })
        return
      }
      if (selectedClaim.appointmentId) {
        const sameProvider =
          selectedClaim.providerId === selectedAppointment.provider.provider_id
        if (!sameProvider) {
          setWarningInfo({
            show: true,
            title: 'Provider mismatch',
            body: 'The provider for the claim does not match the provider associated with the selected appointment.',
          })
          return
        }
      }
    }
    if (step < steps.length) {
      setStep(step + 1)
    }
  }

  const handleReset = (): void => {
    setStep(1)
    const now = new Date().toISOString().split('T')[0]
    setSelectedClaim({
      ...initialClaim,
      id: `${activeTab === 'pre-auth' ? 'PRE' : 'CLM'}000001`,
      createdAt: now,
      updatedAt: now,
    })
    closeModal()
  }

  const handleSubmit = async (): Promise<void> => {
    setShowSubmitConfirmation(true)
  }

  const formRequestData = async (): Promise<any> => {
    // Remove this once we have production ready DentalXChange API
    // Mock service is used to simulate the third-party system while keeping our application aligned with their data structure.
    const dataForMockService = {
      claimDetails: {
        claimId: selectedClaim.providerClaimId,
        payerName: selectedClaim.insuranceCarrier,
        payerIdCode: selectedClaim.insuranceCarrierPayerCode,
        billingProvider: {
          lastName: selectedClinic?.clinic_name,
        },
        renderingProvider: {
          lastName: selectedClaim.providerName
            .replace(/^Dr\.\s*/i, '')
            .trim()
            .split(/\s+/)
            .slice(-1)[0],
          firstName: selectedClaim.providerName
            .replace(/^Dr\.\s*/i, '')
            .trim()
            .split(/\s+/)[0],
        },
        subscriber: {
          lastName: selectedClaim.subscriberLastName,
          firstName: selectedClaim.subscriberFirstName,
          dob: selectedClaim.subscriberDateOfBirth,
          memberId: selectedClaim.subscriberId,
        },
        patient: {
          lastName: selectedClaim.patientLastName,
          firstName: selectedClaim.patientFirstName,
          dob: selectedClaim.patientDateOfBirth,
          memberId: selectedClaim.patientId,
        },
        submittedAmount: Number(selectedClaim.claimAmount),
        paidAmount: 0,
        statusDate: new Date(
          Date.UTC(
            new Date().getUTCFullYear(),
            new Date().getUTCMonth(),
            new Date().getUTCDate(),
          ),
        )
          .toISOString()
          .replace('Z', '-00:00'),
        categoryCodes: [
          {
            code: 'A0',
            statusCode: '16',
            codeDescription:
              'Acknowledgement/Forwarded-The claim/encounter has been forwarded to another entity.',
            statusCodeDescription:
              'Claim/encounter has been forwarded to entity.',
          },
        ],
        hasAttachment: false,
        claimItems: selectedClaim.procedures.map((procedure) => ({
          procedureCode: procedure.procedureCode,
          startDate: procedure.procedureDate.replace(
            /(\d{2})\/(\d{2})\/(\d{4})/,
            '$3-$1-$2T00:00:00-00:00',
          ),
          endDate: procedure.procedureDate.replace(
            /(\d{2})\/(\d{2})\/(\d{4})/,
            '$3-$1-$2T00:00:00-00:00',
          ),
          submittedAmount: procedure.amount,
          statusDate: new Date(
            Date.UTC(
              new Date().getUTCFullYear(),
              new Date().getUTCMonth(),
              new Date().getUTCDate(),
            ),
          )
            .toISOString()
            .replace('Z', '-00:00'),
          categoryCodes: [
            {
              code: 'A0',
              statusCode: '16',
              codeDescription:
                'Acknowledgement/Forwarded-The claim/encounter has been forwarded to another entity.',
              statusCodeDescription:
                'Claim/encounter has been forwarded to entity.',
            },
          ],
        })),
      },
    }
    return {
      claim: {
        providers: [
          ...(() => {
            const billingProvider = providerSettings?.[0].billing_to_provider
            if (billingProvider) {
              return [
                {
                  type: 'BILLING',
                  specialty: 'GP',
                  taxId:
                    billingProvider?.tax_id_number?.trim().replace('-', '') ||
                    '',
                  addresses: [
                    {
                      address1: billingProvider.address,
                      city: billingProvider.city,
                      state: billingProvider.state,
                      zipCode: billingProvider.zip,
                      entityType: '1',
                    },
                  ],
                },
              ]
            } else {
              return []
            }
          })(),
        ],
        payer: {
          payerIdCode: selectedClaim.insuranceCarrierPayerCode,
        },
        patient: {
          memberType: 'PATIENT',
          gender: selectedClaim.patientGender.toUpperCase()[0],
          memberId: selectedClaim.patientId,
          dateOfBirth: moment(
            selectedClaim.patientDateOfBirth,
            'MM/DD/YYYY',
          ).format('YYYY-MM-DD'),
          sequenceCode: 'P',
          relationship: '18',
        },
        subscriber: {
          memberType: 'SUBSCRIBER',
          gender: 'M',
          memberId: selectedClaim.subscriberId,
          dateOfBirth: moment(
            selectedClaim.subscriberDateOfBirth,
            'MM/DD/YYYY',
          ).format('YYYY-MM-DD'),
          sequenceCode: 'P',
          relationship: '18',
        },
        items: [
          ...(() => {
            const procedures = selectedClaim.procedures
            return procedures.map((procedure) => ({
              quantity: 1,
              fee: Number(procedure.amount),
              procedureCode: procedure.procedureCode,
            }))
          })(),
        ],
        type: 'CL',
        providerClaimId: selectedClaim.providerClaimId,
        infoSign: true,
        benefitSign: true,
      },
      ...dataForMockService,
    }
  }

  const formRequestDataForAttachment = (requestData: any): any => ({
    providerClaimId: requestData.claim.providerClaimId,
    payerIdCode: requestData.claim.payer.payerIdCode,
    patient: {
      lastName: selectedClaim.patientLastName,
      firstName: selectedClaim.patientFirstName,
      dateOfBirth: requestData.claim.patient.dateOfBirth,
    },
    subscriber: {
      id: requestData.claim.subscriber.memberId,
      firstName: selectedClaim.subscriberFirstName,
      lastName: selectedClaim.subscriberLastName,
    },
    billingProvider: {
      ...(() => {
        const billingProvider = providerSettings?.[0].billing_to_provider
        if (billingProvider) {
          return {
            npi: billingProvider.npi_number,
            taxId:
              billingProvider?.tax_id_number?.trim().replace('-', '') || '',
            lastName: billingProvider.last_name,
            firstName: billingProvider.first_name,
            taxonomy: 'EIN',
            address1: billingProvider.address,
            city: billingProvider.city,
            state: billingProvider.state,
            zipCode: billingProvider.zip,
          }
        } else {
          return {}
        }
      })(),
    },
    images: [],
    releaseAttachment: true,
  })

  const submitAttachments = async (requestData: any): Promise<void> => {
    if (editingClaim) {
      let hasAttachments = false
      const requestDataForAttachment = formRequestDataForAttachment(requestData)
      if (selectedClaim.attachments.length > 0) {
        await Promise.all(
          selectedClaim.attachments.map(async (attachment) => {
            const base64Image = await fileToBase64(attachment.newFile as File)
            requestDataForAttachment.images.push({
              imageFileAsBase64: base64Image,
            })
            hasAttachments = true
          }),
        )
      }
      if (selectedClaim.procedures.length > 0) {
        await Promise.all(
          selectedClaim.procedures.map(async (procedure) => {
            await Promise.all(
              procedure.procedureAttachments.map(async (attachment) => {
                const base64Image = await fileToBase64(
                  attachment.newFile as File,
                )
                requestDataForAttachment.images.push({
                  imageFileAsBase64: base64Image,
                })
                hasAttachments = true
              }),
            )
          }),
        )
      }
      if (hasAttachments) {
        await submitAttachmentExternal(requestDataForAttachment)
      }
    } else {
      let hasAttachments = false
      const requestDataForAttachment = formRequestDataForAttachment(requestData)
      if (selectedClaim.attachments.length > 0) {
        await Promise.all(
          selectedClaim.attachments.map(async (attachment) => {
            const base64Image = await fileToBase64(attachment.newFile as File)
            requestDataForAttachment.images.push({
              imageFileAsBase64: base64Image,
            })
            hasAttachments = true
          }),
        )
      }
      if (selectedClaim.procedures.length > 0) {
        await Promise.all(
          selectedClaim.procedures.map(async (procedure) => {
            await Promise.all(
              procedure.procedureAttachments.map(async (attachment) => {
                const base64Image = await fileToBase64(
                  attachment.newFile as File,
                )
                requestDataForAttachment.images.push({
                  imageFileAsBase64: base64Image,
                })
                hasAttachments = true
              }),
            )
          }),
        )
      }
      if (hasAttachments) {
        await submitAttachmentExternal(requestDataForAttachment)
      }
    }
  }

  const submitAddClaim = async (): Promise<void> => {
    const requestData = await formRequestData()
    await dispatch(submitClaim(requestData))
      .then(async (state) => {
        const claimsExternal = state.payload.response
        selectedClaim.externalData = claimsExternal
        selectedClaim.userId = user.user_id
        selectedClaim.clinicId = localStorage.getItem('clinic_id') || ''
        const createResponse = await createClaimByPatientId(
          selectedPatient?.patient_id || '',
          selectedClaim,
        )
        const envelope = createResponse as { data?: unknown }
        const newClaimId =
          extractClaimIdFromCreateClaimResponse(createResponse) ??
          extractClaimIdFromCreateClaimResponse(envelope.data)
        const patientIdForNotes =
          selectedClaim.patientIdSystem ?? selectedPatient?.patient_id ?? ''
        if (newClaimId && patientIdForNotes) {
          try {
            await persistUnsavedClaimAttachmentNotes(
              dispatch,
              patientIdForNotes,
              newClaimId,
              selectedClaim.notes,
            )
          } catch (e) {
            logger.error('Failed to persist claim attachment notes:', e)
          }
        }
        submitAttachments(requestData)
      })
      .then(() => {
        console.info('Claim successfully added.')
      })
      .catch((error) => {
        logger.error('Error submitting claim:', error)
      })
  }

  const submitUpdateClaim = async (): Promise<void> => {
    const requestData = await formRequestData()
    await dispatch(submitClaim(requestData))
      .then(async (state) => {
        const claimsExternal = state.payload.response
        selectedClaim.externalData = claimsExternal
        selectedClaim.userId = user.user_id
        await updateClaimById(editingClaim?.id || '', selectedClaim).then(
          async () => {
            const claimUuid =
              getNotesClaimContextId(selectedClaim) ??
              (editingClaim != null
                ? getNotesClaimContextId(editingClaim)
                : undefined)
            const patientIdForNotes =
              selectedClaim.patientIdSystem ?? selectedPatient?.patient_id ?? ''
            if (claimUuid && patientIdForNotes) {
              try {
                await persistUnsavedClaimAttachmentNotes(
                  dispatch,
                  patientIdForNotes,
                  claimUuid,
                  selectedClaim.notes,
                )
              } catch (e) {
                logger.error('Failed to persist claim attachment notes:', e)
              }
            }
            submitAttachments(requestData)
          },
        )
      })
      .then(() => {
        console.info('Claim successfully updated.')
      })
      .catch((error) => {
        logger.error('Error submitting claim:', error)
      })
  }

  const handleConfirmSubmit = async (): Promise<void> => {
    const claimTypeDescription =
      selectedClaim.claimType === 'Claims' ? 'Claim' : 'Pre-Authorization'
    try {
      setShowSubmitConfirmation(false)
      showLoading({
        container: '.appointment-content-container',
        hideElements: ['.claim-feature-grid'],
      })
      if (editingClaim) {
        await submitUpdateClaim().then()
      } else {
        await submitAddClaim().then()
      }
      setTimeout(() => {
        hideLoading('.appointment-content-container')
        showToast(
          createSuccessToast(
            `${claimTypeDescription} submitted successfully`,
            `${claimTypeDescription} has been submitted successfully.`,
          ),
        )
        handleReset()
        refreshClaims()
      }, 2000)
    } catch (error) {
      logger.error('Error submitting claim:', error)
      setTimeout(() => {
        hideLoading('.appointment-content-container')
        showToast(
          createFailedToast(
            `${claimTypeDescription} not submitted`,
            `${claimTypeDescription} not submitted. Please try again.`,
          ),
        )
      }, 2000)
    }
  }

  const handleBack = (): void => {
    if (step > 1) setStep(step - 1)
  }

  const handleUpdateClaim = (updatedClaim: Claim): void => {
    const updatedProcedures = updatedClaim.procedures
    const claimAmount = updatedProcedures.reduce((acc: any, procedure: any) => {
      return acc + Number(procedure.amount)
    }, 0) as number

    setSelectedClaim((prev) => ({
      ...prev,
      ...updatedClaim,
      procedures:
        updatedProcedures && updatedProcedures.length > 0
          ? updatedProcedures
          : [],
      claimAmount: claimAmount.toFixed(2),
    }))
  }

  const handleEditProcedure = (
    procedure: Procedure,
    editMode: boolean,
  ): void => {
    setSelectedProcedure(procedure)
    setEditViewProcedureModal({
      isVisible: true,
      editMode,
    })
  }

  const handleCloseEditProcedureModal = (): void => {
    setEditViewProcedureModal({
      isVisible: false,
      editMode: false,
    })
  }

  useEffect(() => {
    if ((selectedClaim.procedures ?? []).length > 0) {
      const insurancePlanIdSource = (selectedClaim.procedures ?? [])[0]
        .insurance_plan_id
      if (insurancePlanIdSource !== (selectedClaim.insurancePlanId || null)) {
        setSelectedClaim((prevClaim) => ({
          ...prevClaim,
          procedures: prevClaim.procedures.map((p) => ({
            ...p,
            ...getInsuranceFieldValues(
              p.procedureId,
              Number(p.clinic_fee),
              selectedClaim.insurancePlanId,
              insurancePlans,
              selectedProvider.id || '',
            ),
          })),
        }))
      }
    }
  }, [selectedClaim.insurancePlanId])

  const handleSaveDraft = async (): Promise<void> => {
    try {
      const claimTypeDescription =
        selectedClaim.claimType === 'Claims' ? 'Claim' : 'Pre-Authorization'
      showLoading({
        container: '.appointment-content-container',
        hideElements: ['.claim-feature-grid'],
      })
      selectedClaim.userId = user.user_id
      selectedClaim.clinicId = localStorage.getItem('clinic_id') || ''
      await saveDraftClaim(selectedClaim)
      setTimeout(() => {
        hideLoading('.appointment-content-container')
        showToast(
          createSuccessToast(
            `${claimTypeDescription} saved as draft successfully`,
            `${claimTypeDescription} has been saved as draft successfully.`,
          ),
        )
        handleReset()
        refreshClaims()
      }, 2000)
    } catch (error) {
      logger.error('Error submitting claim:', error)
      setTimeout(() => {
        hideLoading('.appointment-content-container')
        showToast(
          createFailedToast(
            'Claim not saved as draft',
            'Claim not saved as draft. Please try again.',
          ),
        )
      }, 2000)
    }
  }

  return (
    <>
      <Modal
        show={isVisible}
        onHide={handleReset}
        backdrop="static"
        keyboard={false}
        size="lg"
        enforceFocus={false}
        className={`add-edit-claims-modal ${isApproved ? 'add-claims-modal' : ''} ${step === 2 ? 'add-claims-modal-procedures' : ''} ${step === 3 ? 'add-claims-modal-attachments' : ''} ${step === 4 ? 'add-claims-modal-review' : ''}`}
      >
        <div style={{ position: 'relative' }}>
          {(showSubmitConfirmation || warningInfo.show) && (
            <div className="modal-dark-overlay"></div>
          )}
          <Modal.Header
            className="bg-custom3 add-claims-modal-header"
            closeButton
          >
            <Modal.Title>
              {editingClaim
                ? activeTab === 'pre-auth'
                  ? 'Edit pre-auth'
                  : 'Edit claim'
                : activeTab === 'pre-auth'
                  ? 'Add pre-auth'
                  : 'Add claim'}
            </Modal.Title>
          </Modal.Header>

          <Modal.Body className="pt-2 pb-2 add-claims-modal-body">
            <Stepper steps={steps} currentStep={step - 1} />
            <div className="tab-content mt-3">
              {step === 1 ? (
                <ClaimDetailsTab
                  selectedClaim={selectedClaim}
                  insurance={{
                    data: insurancePlans,
                    selected: selectedInsurancePlan,
                    handleSelect: handleInsuranceSelect,
                  }}
                  provider={{
                    data: providers,
                    selected: selectedProvider,
                    handleSelect: handleProviderSelect,
                    handleNameChange: providerNameHandleChange,
                  }}
                  onChangeClaim={handleUpdateClaim}
                />
              ) : step === 2 ? (
                <ClaimProcedureTab
                  selectedClaim={selectedClaim}
                  appointment={{
                    data: appointments,
                    selected: selectedAppointment,
                    handleSelect: handleAppointmentSelect,
                  }}
                  treatmentPlan={{
                    data: treatmentPlans,
                    selected: selectedTreatmentPlan,
                    handleSelect: handleTreatmentPlanSelect,
                  }}
                  handleClaimSourceChange={handleClaimSourceChange}
                  onChangeClaim={handleUpdateClaim}
                  onEditProcedure={handleEditProcedure}
                />
              ) : step === 3 ? (
                <ClaimAttachmentTab
                  selectedClaim={selectedClaim}
                  onChangeClaim={handleUpdateClaim}
                  isEditingExistingClaim={editingClaim != null}
                />
              ) : (
                <ClaimReviewTab
                  claimData={selectedClaim}
                  onEditClick={() => setStep(1)}
                  onClaimNotesSynced={(notes) => {
                    setSelectedClaim((prev) => ({ ...prev, notes }))
                  }}
                />
              )}
            </div>
          </Modal.Body>

          <Modal.Footer className="d-inline-block w-100">
            <Row>
              <Col className="text-start ps-0">
                <Button
                  size="md"
                  className="button-link"
                  onClick={handleSaveDraft}
                >
                  Save as draft
                </Button>
              </Col>
              <Col className="text-end pe-0">
                {step > 1 && (
                  <>
                    <Button
                      size="md"
                      className="button-secondary me-2"
                      onClick={handleBack}
                    >
                      Previous
                    </Button>
                  </>
                )}

                {step < steps.length ? (
                  <>
                    <Button
                      size="md"
                      className="button-primary"
                      onClick={handleNext}
                    >
                      Next
                    </Button>
                  </>
                ) : (
                  <Button
                    size="md"
                    className="button-primary"
                    onClick={isApproved ? closeModal : handleSubmit}
                  >
                    {isApproved
                      ? 'Close'
                      : editingClaim
                        ? 'Update claim'
                        : 'Submit'}
                  </Button>
                )}
              </Col>
            </Row>
          </Modal.Footer>
        </div>
      </Modal>
      {selectedProcedure && editViewProcedureModal.isVisible && (
        <EditClaimProcedureModal
          isVisible={editViewProcedureModal.isVisible}
          editMode={editViewProcedureModal.editMode}
          closeModal={handleCloseEditProcedureModal}
          procedure={selectedProcedure}
          selectedClaim={selectedClaim}
          onSave={(updatedProcedure) => {
            setSelectedClaim((prevClaim) => ({
              ...prevClaim,
              procedures: prevClaim.procedures.map((p) =>
                p.procedureId === updatedProcedure.procedureId &&
                (updatedProcedure.phaseName
                  ? p.phaseName === updatedProcedure.phaseName
                  : true)
                  ? updatedProcedure
                  : p,
              ),
            }))
            handleCloseEditProcedureModal()
          }}
        />
      )}
      <ConfirmationModal
        show={warningInfo.show}
        onHide={() => setWarningInfo({ show: false, title: '', body: '' })}
        title={warningInfo.title}
        className="confirmation-modal--alert"
        headerClassName="confirmation-modal__header"
        size="md"
        body={
          <div className="confirmation-modal__body">
            <p className="confirmation-modal__title">{warningInfo.title}</p>
            <p className="confirmation-modal__message">{warningInfo.body}</p>
          </div>
        }
        footer={
          <div className="confirmation-modal__footer">
            <Button
              size="md"
              onClick={() =>
                setWarningInfo({ show: false, title: '', body: '' })
              }
              className="button-primary"
            >
              Got it
            </Button>
          </div>
        }
      />
      <DsModal
        open={showSubmitConfirmation}
        onClose={() => setShowSubmitConfirmation(false)}
        size="xs"
        title={
          editingClaim
            ? activeTab === 'pre-auth'
              ? 'Update pre-auth?'
              : 'Update claim?'
            : activeTab === 'pre-auth'
              ? 'Submit pre-auth?'
              : 'Submit claim?'
        }
        description={
          editingClaim
            ? activeTab === 'pre-auth'
              ? 'You are about to update an existing pre-auth. Please double-check all details before proceeding.'
              : 'You are about to update an existing claim. Please double-check all details before proceeding.'
            : activeTab === 'pre-auth'
              ? 'You are about to submit a new pre-auth. Please review all information carefully before submitting.'
              : 'You are about to submit a new claim. Please review all information carefully before submitting.'
        }
        primaryLabel="Proceed"
        secondaryLabel="Cancel"
        onPrimaryClick={handleConfirmSubmit}
        onSecondaryClick={() => setShowSubmitConfirmation(false)}
      />
    </>
  )
}

export default AddClaimsModal
